<template>
  <div>
    <div
      class="q-mb-md q-mt-md text-right"
    >
      <q-btn-toggle
        v-model="viewType"
        push
        size="sm"
        toggle-color="primary"
        :options="viewTypeOptions"
      />
    </div>
    <div v-if="hasNoData && !isLoading">
      {{ $t('programs.textNoData') }}
    </div>
    <div
      v-else-if="showDataTable"
      ref="trialDataContainer"
      full-width
      style="overflow-x:scroll; -webkit-overflow-scrolling: touch;"
    >
      <table class="q-table trialdata">
        <thead>
          <tr>
            <td
              v-if="hasMoreData || isLoading"
            />
            <td
              v-for="session in sessionTrialData"
              :key="session.session"
              :colspan="viewType === 'allTrials' ? session.data.length : 1"
              class="text-center"
            >
              <div>
                {{ getLocaleDateString(session.runTimestamp) }}
              </div>
              <div class="ellipsis">
                {{ session.sessionName }}
              </div>
              <div class="ellipsis">
                {{ session.runByName }}
              </div>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="viewType === 'allTrials'"
          >
            <td
              v-if="hasMoreData || isLoading"
              rowspan="100%"
              class="text-center"
            >
              <q-btn
                icon="more"
                :label="$t('programs.targetStatistics.buttonLoadMoreLabel')"
                size="sm"
                :loading="isLoading"
                no-wrap
                flat
                color="dark-variant"
                @click="onLoadMoreData"
              />
            </td>
            <template
              v-for="session in sessionTrialData"
              :key="session.id"
            >
              <template
                v-for="(trial, idx) in session.data"
                :key="trial.id"
              >
                <td class="text-center">
                  #{{ idx+1 }}
                </td>
              </template>
            </template>
          </tr>
          <tr>
            <td
              v-if="(hasMoreData || isLoading) && (viewType !== 'allTrials')"
              rowspan="100%"
              class="text-center"
            >
              <q-btn
                icon="more"
                :label="$t('programs.targetStatistics.buttonLoadMoreLabel')"
                size="sm"
                flat
                :loading="isLoading"
                no-wrap
                color="dark-variant"
                @click="onLoadMoreData"
              />
            </td>
            <template
              v-for="session in sessionTrialData"
              :key="session.id"
            >
              <template
                v-for="(trial, idx) in session.data"
                :key="trial.id"
              >
                <td
                  v-if="viewType === 'allTrials' || (idx === 0)"
                  class="text-center"
                  :class="{
                    'probe': idx === 0,
                    'non-probe': idx !== 0
                  }"
                  :style="getStyleForTrialData(trial)"
                >
                  <template v-if="isNumber(trial.duration)">
                    {{ formatDuration(trial.duration) }}
                  </template>
                  <q-tooltip
                    v-if="trial.promptLevelDetails"
                  >
                    {{
                      $t('programs.targetStatistics.tooltipTrialText')
                    }}: #{{ idx+1 }}<br>
                    {{ trial.promptLevelDetails.trialOutcome }}<br>
                  </q-tooltip>
                </td>
              </template>
            </template>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="showInsights">
      <insights-graph
        v-if="hasGraphInfo"
        :graph-info="graphInfo"
        :client-id="program.client"
        :init-program-id="program.id"
        :init-draft-program-targets="draftProgramTargets"
        disable-chart-name-edit
        :chart-name="programGraphName"
        :embed-chart-title="!!customProgramGraphName"
        :enable-min-trials-filter="enableMinTrialsFilter"
        hide-chart-name
        hide-settings
        :hide-favorite="hideFavorite"
        :favorite-icon="favoriteIcon"
        :favorite-tooltip="favoriteTooltip"
        :filter-data-source-ids="[
          'dsTargets', 'dsSessionData', 'dsSessionTrialData', 'dsBehaviorIncidentDataFlat'
        ]"
        @change-date-range="onChangeDateRange"
        @change-data-series-settings="onChangeDataSeriesSettings"
        @favorite="onFavorite"
      />
    </div>
  </div>
</template>
<script>
import InsightsGraph from 'components/Insights/InsightsGraph';
import { chain, find, get, head, isEmpty, isNumber, omit, size } from 'lodash';
import { mapActions } from 'pinia';
import { measurementMetrics } from 'src/boot/globals';
import { notifySuccess } from 'src/composables/interactions';
import { useTime } from 'src/composables/time';
import Insights from 'src/mixins/Insights';
import Targets from 'src/mixins/Targets';
import { programsTargets } from 'src/store/programsTargets';
import { getTargetProgressSettingsKey, getTextColor, isMinTrialsFilterApplicable } from 'src/utils/TargetUtils';

export default {
  name: 'TargetStatistics',

  components: { InsightsGraph },

  mixins: [Targets, Insights],

  props: {
    program: {
      type: Object,
      required: true,
    },
    programTargets: {
      type: Array,
      required: true,
    },
    target: {
      type: Object,
      required: true,
    },
    isRatingScale: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update-target-progress-settings',
  ],

  setup() {
    const { getLocaleDateString } = useTime();
    return { getLocaleDateString };
  },

  data() {
    return {
      isLoading: false,
      unsavedGraphChanges: false,

      graphInfo: null,

      hasMoreData: false,
      lastSessionIdx: 0,
      sessionTrialData: [],

      viewType: null,
      targetMetric: null,
      enableMinTrialsFilter: false,
    };
  },

  computed: {
    hasNoData() {
      return this.showTrials && (size(this.sessionTrialData) === 0);
    }, // hasNoData()

    hasGraphInfo() {
      return this.graphInfo !== null;
    }, // hasGraphInfo()

    hideFavorite() {
      const viewTypeKey = this.viewType || 'default';
      return (
        chain(this.target).get(['progressSettings', viewTypeKey]).isEmpty().value()
        && !this.unsavedGraphChanges
      );
    }, // hideFavorite()

    draftProgramTargets() {
      return this.programTargets.filter((target) => {
        return ['create', 'update'].includes(get(target, 'editStatus', ''));
      });
    },

    customProgramGraphName() {
      return get(this.graphInfo, ['dataSeriesSettings', 'chartSettings', 'chartTitle']);
    }, // customProgramGraphName()

    programGraphName() {
      if (!this.customProgramGraphName) {
        return get(this.target, 'label', null);
      }

      return this.customProgramGraphName;
    }, // programGraphName()

    favoriteIcon() {
      return this.unsavedGraphChanges ? 'star_outline' : 'star';
    }, // favoriteIcon()

    favoriteTooltip() {
      return this.unsavedGraphChanges
        ? this.$t('programs.targetStatistics.tooltipSaveProgressChart')
        : this.$t('programs.targetStatistics.tooltipRevertToDefault');
    }, // favoriteTooltip()

    showTrials() {
      return get(this.currentViewTypeOption, 'showTrials', false);
    }, // showTrials()

    showInsights() {
      return get(this.currentViewTypeOption, 'showInsights', false);
    }, // showInsights()

    showDataTable() {
      const activeValue = get(this.currentViewTypeOption, 'value');
      return ['allTrials', 'coldProbes'].includes(activeValue);
    }, // showDataTable()

    viewTypeOptions() {
      if (this.isRatingScale) {
        return [
          {
            label: this.$t('programs.targetStatistics.buttonToggleAverageRatingLabel'),
            value: 'spAverageRating',
            icon: 'timeline',
            showInsights: true,
          },
          {
            label: this.$t('programs.targetStatistics.buttonToggleAllTrialsLabel'),
            value: 'allTrials',
            icon: 'update',
            showTrials: true,
          },
          {
            label: this.$t('programs.targetStatistics.buttonToggleColdProbesLabel'),
            value: 'coldProbes',
            icon: 'visibility',
            showTrials: true,
          },
        ];
      }

      const programType = get(this.program, 'type');
      let dataType;
      if (programType === 'simple') {
        dataType = get(this.target, 'dataType');
      } else if (programType !== 'taskAnalysis') {
        // Targets for Task Analysis programs are (currently!) graphed with default settings
        dataType = programType;
      }
      const primaryMetric = this.getTargetMetric(this.target, this.program);
      const defaultLabel = measurementMetrics
        .find((metric) => (metric.id === primaryMetric))?.label || null;
      switch (dataType) {
        case 'rate':
          return [
            {
              label: defaultLabel || this.$t('programs.targetStatistics.buttonToggleRateLabel'),
              value: 'default',
              icon: 'timeline',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleFrequencyObservationsLabel'),
              value: 'frequencyObservations',
              icon: 'update',
              showInsights: true,
            },
          ];
        case 'frequency':
          if (primaryMetric === 'percentCorrect') {
            return [
              {
                label: defaultLabel || this.$t('programs.targetStatistics.buttonToggleSuccessRateLabel'),
                value: 'default',
                icon: 'timeline',
                showInsights: true,
              },
              {
                label: this.$t('programs.targetStatistics.buttonToggleAllTrialsLabel'),
                value: 'allTrials',
                icon: 'update',
                showTrials: true,
              },
              {
                label: this.$t('programs.targetStatistics.buttonToggleColdProbesLabel'),
                value: 'coldProbes',
                icon: 'visibility',
                showTrials: true,
              },
            ];
          }
          if (['ratePerHour', 'ratePerMinute'].includes(primaryMetric)) {
            return [
              {
                label: defaultLabel || this.$t('programs.targetStatistics.buttonToggleRateLabel'),
                value: 'default',
                icon: 'timeline',
                showInsights: true,
              },
              {
                label: this.$t('programs.targetStatistics.buttonToggleFrequencyObservationsLabel'),
                value: 'frequencyObservations',
                icon: 'update',
                showInsights: true,
              },
            ];
          }
          return [
            {
              label: defaultLabel || this.$t('programs.targetStatistics.buttonToggleRateLabel'),
              value: 'frequency',
              icon: 'timeline',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleFrequencyObservationsLabel'),
              value: 'frequencyObservations',
              icon: 'update',
              showInsights: true,
            },
          ];
        case 'duration':
          return [
            {
              label: this.$t('programs.targetStatistics.buttonToggleTotalDurationLabel'),
              value: 'totalDuration',
              icon: 'update',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleMinDurationLabel'),
              value: 'minDuration',
              icon: 'minimize',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleMaxDurationLabel'),
              value: 'maxDuration',
              icon: 'maximize',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleAvgDurationLabel'),
              value: 'avgDuration',
              icon: 'equalizer',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleAllTrialsLabel'),
              value: 'allTrials',
              icon: 'update',
              showTrials: true,
            },
          ];
        case 'taskAnalysis':
          return [
            {
              label: this.$t('programs.targetStatistics.buttonToggleTotalTaskLabel'),
              value: 'totalTask',
              icon: 'show_chart',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleSuccessPerTargetStepLabel'),
              value: 'successPerTargetStep',
              icon: 'format_list_bulleted',
              showInsights: true,
            },
          ];
        default:
          return [
            {
              label: defaultLabel || this.$t('programs.targetStatistics.buttonToggleSuccessRateLabel'),
              value: 'default',
              icon: 'timeline',
              showInsights: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleAllTrialsLabel'),
              value: 'allTrials',
              icon: 'update',
              showTrials: true,
            },
            {
              label: this.$t('programs.targetStatistics.buttonToggleColdProbesLabel'),
              value: 'coldProbes',
              icon: 'visibility',
              showTrials: true,
            },
          ];
      }
    }, // viewTypeOptions

    defaultViewType() {
      return getTargetProgressSettingsKey(
        this.target.primaryMetric,
        this.target.dataType,
        this.program.primaryMetric,
        this.program.type,
        this.isRatingScale,
      );
    }, // defaultViewType()

    currentViewTypeOption() {
      const options = this.viewTypeOptions;
      return find(options, { value: this.viewType }) || head(options);
    }, // currentViewTypeOption()

  },

  watch: {

    isRatingScale: {
      handler() {
        this.viewType = this.defaultViewType;
      },
    },

    viewType: {
      immediate: true,
      deep: false,
      async handler() {
        if (this.showInsights) {
          this.initGraph();
        }

        if (this.showTrials && isEmpty(this.sessionTrialData)) {
          await this.loadData();

          this.$nextTick(() => {
            this.scrollTrialData();
          });
        }
      },
    },
  },

  async mounted() {
    this.targetMetric = this.getTargetMetric(this.target, this.program);
    this.viewType = this.defaultViewType;
  },

  methods: {
    isNumber,
    ...mapActions(programsTargets, ['setProgramTargetProperties']),

    async onLoadMoreData() {
      this.loadData();
    }, // onLoadMoreData()

    onChangeDateRange(newDateRange) {
      this.graphInfo.dateRange = newDateRange;
      this.checkProgressSettingsChanged();
    }, // onChangeDateRange()

    onChangeDataSeriesSettings(newDataSeriesSettings) {
      this.graphInfo.dataSeriesSettings = newDataSeriesSettings;
      this.checkProgressSettingsChanged();

      this.updateMinTrialsFilter();
    }, // onChangeDataSeriesSettings()

    updateMinTrialsFilter() {
      const shouldEnable = this.graphInfo?.dataSeriesSettings?.xAxisAggregateBy === 'sessionId'
        && isMinTrialsFilterApplicable(this.target.dataType, this.targetMetric);

      if (shouldEnable !== this.enableMinTrialsFilter) {
        this.enableMinTrialsFilter = shouldEnable;
      }
    }, // updateMinTrialsFilter()

    async onFavorite() {
      if (!this.unsavedGraphChanges) {
        // Revert
        return this.$q.dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('programs.targetStatistics.confirmRevertToDefault'),
          cancel: true,
          persistent: true,
        }).onOk(async () => {
          try {
            this.$q.loading.show();
            const viewTypeKey = this.viewType || 'default';

            if (this.target.editStatus !== 'create') {
              // Flip unsaved changes to ensure that hideFavorite is reactive to target changes
              this.unsavedGraphChanges = true;
              await this.setProgramTargetProperties({
                programId: this.program.id,
                targetId: this.target.id,
                programTargetObj: {
                  progressSettings: {
                    [viewTypeKey]: null,
                  },
                },
              });
            }

            this.$emit('update-target-progress-settings', {
              targetId: this.target.id,
              progressSettings: {
                [viewTypeKey]: null,
              },
            });
            this.unsavedGraphChanges = false;
          } finally {
            this.$q.loading.hide();
          }
          this.initGraph();
        });
      }

      const dateRange = {
        ...this.graphInfo.dateRange,
      };
      if (dateRange.preset !== 'custom') {
        delete dateRange.fromTimestamp;
        delete dateRange.toTimestamp;
      }

      const viewTypeKey = this.viewType || 'default';
      const progressSettings = {
        dataSeriesSettings: omit(this.graphInfo.dataSeriesSettings, '_localFilterUpdate'),
        dateRange,
      };

      if (this.target.editStatus !== 'create') {
        await this.setProgramTargetProperties({
          programId: this.program.id,
          targetId: this.target.id,
          programTargetObj: {
            progressSettings: {
              [viewTypeKey]: progressSettings,
            },
          },
        });
      }

      this.$emit('update-target-progress-settings', {
        targetId: this.target.id,
        progressSettings: {
          [viewTypeKey]: progressSettings,
        },
      });

      this.unsavedGraphChanges = false;

      notifySuccess(this.$t('programs.targetStatistics.notifyGraphSettingsSaved'));
    }, // onFavorite()

    checkProgressSettingsChanged() {
      const viewTypeKey = this.viewType || 'default';
      this.unsavedGraphChanges = (
        get(this.target, [viewTypeKey, 'dateRange', 'preset'])
        !== get(this.graphInfo, ['dateRange', 'preset'])
      )
      || (
        !chain(this.target)
          .get([viewTypeKey, 'progressSettings', 'dataSeriesSettings'])
          .isEqual(get(this.graphInfo, ['dataSeriesSettings']))
          .value()
      );
    }, // checkProgressSettingsChanged()

    async loadData() {
      try {
        this.isLoading = true;
        // Load one extra session to see if we should show "Load more" option
        const loadSessions = 16;
        const skipSessions = size(this.sessionTrialData) || null;
        const loadedData = await this.loadTargetData(
          this.program,
          this.target,
          { loadSessions, skipSessions, loadPromptLevels: true, addOverallIndexes: false },
        );
        this.hasMoreData = (loadSessions === size(loadedData));

        if (this.hasMoreData) {
          this.sessionTrialData = chain(loadedData).tail().concat(this.sessionTrialData).value();
        } else {
          this.sessionTrialData = chain(loadedData).concat(this.sessionTrialData).value();
        }
      } finally {
        this.isLoading = false;
      }
    }, // loadData()

    initGraph() {
      if (!this.program) {
        return;
      }

      this.graphInfo = this.getTargetProgressSettings(this.program, this.target, this.viewType);
      this.unsavedGraphChanges = false;

      this.updateMinTrialsFilter();
    }, // initGraph()

    getTargetProgressSettings(program, target, viewType, defaultDateRangePreset = 'prev-90-days') {
      let viewTypeKey = viewType || 'default';
      if (this.isRatingScale && viewTypeKey === 'default') {
        viewTypeKey = 'spAverageRating';
      }
      const targetProgressSettings = get(target, ['progressSettings', viewTypeKey]);
      if (targetProgressSettings) {
        return {
          dateRange: this.applyDateRangePreset(targetProgressSettings.dateRange),
          dataSeriesSettings: targetProgressSettings.dataSeriesSettings,
        };
      }

      const dataSeriesSettings = this
        .getDefaultTargetDataSeriesSettings(target, this.programTargets, program, viewTypeKey);
      return {
        dateRange: this.applyDateRangePreset({ preset: defaultDateRangePreset }),
        dataSeriesSettings: {
          ...dataSeriesSettings,
          analysis: {
            ...get(dataSeriesSettings, 'analysis', {}),
            programStatusPhaseLines: false,
          },
        },
      };
    }, // getTargetProgressSettings()

    scrollTrialData() {
      if (this.$refs.trialDataContainer) {
        this.$refs.trialDataContainer.scrollLeft = this.$refs.trialDataContainer.scrollWidth;
      }
    }, // scrollTrialData()

    getStyleForTrialData(trial) {
      const promptLevelColor = this.getPromptLevelColor(trial.promptLevelDetails, trial.scoreValue);
      if (isNumber(trial.duration)) {
        return `background-color:${promptLevelColor};color:${getTextColor(promptLevelColor)}`;
      }
      return `background-color:${promptLevelColor}`;
    },

  },

};
</script>
<style scoped>
.q-table {
  border-collapse: collapse;
  font-size:0.9em;
}

.q-table td {
  border:solid lightgray 1px;
  padding:5px;
  min-width:15px;
  height:30px;
  font-size:0.9em;
}

.q-table td.trial_perfect {
  background-color: #c1f4cd;
  border: solid #21ba45 1px;
}
.q-table td.trial_partial {
  background-color: #f9e09b;
  border: solid #f2c037 1px;
}
.q-table td.trial_zero {
  background-color: #ffc0c6;
  border: solid #c10015 1px;
}

</style>
